import React, { useEffect, useState } from 'react';

/**
 * MutationFeedbackToast
 *
 * Renders a brief slide-up list of micro-feedback cards when the AI
 * makes changes to the resume. Each card shows what was updated and
 * any ATS score improvement. Auto-dismisses after 3.5s.
 *
 * Props:
 *   items  — [{ label: string, atsDelta: number|null }]
 *   theme  — 'dark' | 'light'
 */
export default function MutationFeedbackToast({ items = [], theme = 'dark' }) {
  const [visible, setVisible] = useState(false);
  const T = theme === 'dark';

  useEffect(() => {
    if (items.length > 0) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [items]);

  if (!visible || items.length === 0) return null;

  return (
    <div
      style={{
        padding: '4px 16px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        fontFamily: "'Inter', sans-serif",
        animation: 'slideUpFade 0.3s ease',
      }}
    >
      <style>{`
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {items.map((item, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 10px',
            borderRadius: 8,
            background: T ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)',
            border: T
              ? '1px solid rgba(16,185,129,0.2)'
              : '1px solid rgba(16,185,129,0.25)',
            animation: `slideUpFade 0.3s ease ${i * 0.07}s both`,
          }}
        >
          {/* Green checkmark icon */}
          <span style={{
            width: 16, height: 16, borderRadius: '50%',
            background: '#10b981',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 0 6px rgba(16,185,129,0.4)',
          }}>
            <svg width={8} height={8} fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </span>

          {/* Label */}
          <span style={{
            fontSize: 11.5,
            fontWeight: 500,
            color: T ? 'rgba(255,255,255,0.80)' : '#374151',
            flex: 1,
          }}>
            {item.label}
          </span>

          {/* ATS delta badge */}
          {item.atsDelta != null && item.atsDelta > 0 && (
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#10b981',
              background: T ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.10)',
              padding: '1px 6px',
              borderRadius: 20,
            }}>
              +{item.atsDelta} ATS
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
